import { createContext, useContext, useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";

const CompanyContext = createContext(null);

const DEFAULT_COMPANY = {
  name: "",
  logoUrl: "",
  email: "",
  phone: "",
  address: "",
  website: "",
  accentColor: "#4F46E5",
  invoiceFooterNote: "Thank you for your business!",
  taxId: "",
  profileExists: false,
};

const CLOUDINARY_CLOUD_NAME = "dzxigzuzf";
const CLOUDINARY_UPLOAD_PRESET = "paylora_logos";

export function CompanyProvider({ children }) {
  const { user } = useAuth();
  const [company, setCompany] = useState(DEFAULT_COMPANY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setCompany(DEFAULT_COMPANY);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const snap = await getDoc(doc(db, "users", user.uid, "company", "profile"));
      if (snap.exists()) {
        setCompany({ ...DEFAULT_COMPANY, ...snap.data(), profileExists: true });
      } else {
        setCompany({ ...DEFAULT_COMPANY, profileExists: false });
      }
      setLoading(false);
    })();
  }, [user]);

  async function updateCompany(updates) {
    if (!user) throw new Error("Not authenticated");
    const next = { ...company, ...updates, profileExists: true };
    setCompany(next);
    await setDoc(doc(db, "users", user.uid, "company", "profile"), next, { merge: true });
    return next;
  }

  async function uploadLogo(file) {
    if (!user) throw new Error("Not authenticated");
    if (!file.type.startsWith("image/")) throw new Error("Logo must be an image file");
    if (file.size > 5 * 1024 * 1024) throw new Error("Logo must be under 5MB");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: "POST", body: formData }
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err?.error?.message || "Logo upload failed");
    }

    const data = await response.json();
    await updateCompany({ logoUrl: data.secure_url });
    return data.secure_url;
  }

  async function removeLogo() {
    await updateCompany({ logoUrl: "" });
  }

  return (
    <CompanyContext.Provider value={{ company, loading, updateCompany, uploadLogo, removeLogo }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
  return ctx;
}
